/**
 * Weekly digest public web page — /weekly/:slug
 *
 * Mirrors the email content (same `db.weekly_digests` doc) but rendered
 * with full JSON-LD so AI search and Google can cite individual weeks.
 *
 * URL shape: /weekly/2026-W21
 *
 * SEO emits three nested schemas in one <script>:
 *   1. BreadcrumbList — Home › Weekly Digest › Week 21, 2026
 *   2. CollectionPage — the digest itself, with `datePublished`
 *   3. ItemList — the products / partners listed inside
 *
 * Each individual item also emits its own SoftwareApplication schema so
 * Perplexity / Bing Copilot / ChatGPT can attribute the listing back to
 * the canonical /product/* or /featured/* URL.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEOHead from '@/components/SEOHead';
import { Loader2, ArrowLeft, Sparkles, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = { product: 'Creator', affiliate: 'Partner', platform: 'Featured' };

export default function WeeklyDigest() {
  const { slug } = useParams();
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API_URL}/api/weekly/${slug}`)
      .then((res) => { if (mounted) { setDigest(res.data); setError(null); } })
      .catch((err) => {
        if (!mounted) return;
        setError(err.response?.status === 404 ? 'This week\'s digest has not been published yet.' : 'Could not load this digest.');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (error || !digest) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
        <p className="text-neutral-400 text-sm mb-4">{error || 'Digest not found'}</p>
        <Link to="/weekly" className="text-[#00F0FF] underline text-sm">← Browse all weekly digests</Link>
      </div>
    );
  }

  // Group items by category for the layout — preserves the same order
  // the backend determined (busiest category first).
  // For preview/staging envs, rewrite logos that point at the prod
  // domain to paths the current backend can serve, so screenshots and
  // staging look right. Email recipients always get the prod URL.
  const _rewriteLogo = (url) => {
    if (!url) return url;
    return url.replace(/^https:\/\/theailedger\.ai\/partners\//, `${API_URL}/partners/`)
              .replace(/^https:\/\/theailedger\.ai\/og-product-default\.png/, `${API_URL}/og-product-default.png`);
  };
  const groups = digest.categories.map((cat) => ({
    name: cat,
    items: digest.items.filter((it) => it.category === cat).map((it) => ({
      ...it,
      logo_url: _rewriteLogo(it.logo_url),
    })),
  }));

  // Build canonical URL + JSON-LD payloads.
  const canonicalUrl = `https://theailedger.ai/weekly/${digest.slug}`;
  const pageTitle = `Top AI tools — Week ${String(digest.iso_week).padStart(2, '0')}, ${digest.iso_year}`;
  const pageDescription = `${digest.fresh_count} new AI tools added across ${digest.categories.length} categories on theailedger.ai for week ${digest.iso_week} of ${digest.iso_year}.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://theailedger.ai' },
          { '@type': 'ListItem', position: 2, name: 'Weekly Digest', item: 'https://theailedger.ai/weekly' },
          { '@type': 'ListItem', position: 3, name: `Week ${digest.iso_week}, ${digest.iso_year}`, item: canonicalUrl },
        ],
      },
      {
        '@type': 'CollectionPage',
        '@id': canonicalUrl,
        name: pageTitle,
        description: pageDescription,
        url: canonicalUrl,
        datePublished: digest.completed_at || digest.created_at,
        isPartOf: { '@type': 'WebSite', name: 'theailedger.ai', url: 'https://theailedger.ai' },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: digest.items.length,
          itemListElement: digest.items.map((it, i) => ({
            '@type': 'ListItem', position: i + 1,
            url: it.permalink,
            item: {
              '@type': 'SoftwareApplication',
              name: it.name,
              description: it.tagline,
              applicationCategory: it.category,
              image: it.logo_url,
              url: it.permalink,
            },
          })),
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="weekly-digest-page">
      <SEOHead
        title={`${pageTitle} · theailedger.ai`}
        description={pageDescription}
        path={`/weekly/${digest.slug}`}
        schema={jsonLd}
      />

      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 pt-12 pb-6">
        <Link to="/weekly" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-[#00F0FF] mb-6" data-testid="weekly-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All weekly digests
        </Link>
        <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#00F0FF] mb-2">
          Weekly digest · Week {String(digest.iso_week).padStart(2, '0')}, {digest.iso_year}
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-3">
          Top AI tools this week
        </h1>
        <p className="text-neutral-400 text-base leading-relaxed max-w-2xl">
          <span className="text-white font-semibold">{digest.fresh_count} new {digest.fresh_count === 1 ? 'tool' : 'tools'}</span> added across {digest.categories.length} categor{digest.categories.length === 1 ? 'y' : 'ies'} — creator products, partners, and featured platforms in one scroll.
        </p>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 pb-24">
        {groups.map((g) => (
          <section key={g.name} className="mt-10" data-testid={`weekly-category-${g.name.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#00F0FF] mb-4">
              {g.name} · {g.items.length}
            </div>
            <ul className="divide-y divide-white/[0.06]">
              {g.items.map((it) => (
                <li key={`${it.type}-${it.id}`} className="py-4 flex gap-4 items-start" data-testid={`weekly-item-${it.id}`}>
                  <a href={it.permalink} target={it.type === 'affiliate' ? '_blank' : '_self'} rel={it.type === 'affiliate' ? 'noopener noreferrer' : ''} className="block w-14 h-14 rounded-xl overflow-hidden bg-neutral-900 flex-shrink-0">
                    {it.logo_url && <img src={it.logo_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
                  </a>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {it.fresh ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider rounded bg-[#00F0FF] text-black">
                          <Sparkles className="w-2.5 h-2.5" /> New
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-white/5 text-neutral-400">
                          <Flame className="w-2.5 h-2.5" /> Trending
                        </span>
                      )}
                      <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{TYPE_LABEL[it.type]}</span>
                    </div>
                    <a href={it.permalink} target={it.type === 'affiliate' ? '_blank' : '_self'} rel={it.type === 'affiliate' ? 'noopener noreferrer' : ''} className="text-white font-bold text-base hover:text-[#00F0FF] block leading-tight">
                      {it.name}
                    </a>
                    <p className="text-sm text-neutral-400 mt-1 leading-relaxed">{it.tagline}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <div className="mt-16 pt-8 border-t border-white/[0.08] flex flex-wrap items-center gap-3">
          <Link to="/weekly">
            <Button variant="outline" className="border-white/15 hover:bg-white/5" data-testid="weekly-all-digests-btn">
              Browse all weekly digests
            </Button>
          </Link>
          <Link to="/featured">
            <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90" data-testid="weekly-featured-btn">
              Featured partner directory →
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
